/**
 * Secure API Client Utility
 *
 * Ensures all requests to the server include the Firebase ID token
 * (JWT access token) instead of sending the password.
 *
 * Token is automatically obtained from Firebase and added to all requests.
 */

import { getAuth } from 'firebase/auth';
import { logger } from './logger';

const API_BASE = process.env.REACT_APP_SERVER_URL
  ? process.env.REACT_APP_SERVER_URL.replace(/\/$/, '')
  : '';

/**
 * Get the current Firebase ID token (JWT)
 * This token is used for all authenticated API requests
 */
async function getAccessToken() {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    // Use cached token (auto-refreshes when expired). Do NOT force-refresh
    // on every call — that makes a network round-trip each time and causes
    // "Failed to fetch" if Google's token endpoint is slow or unreachable.
    // Force-refresh happens once at login (ProtectedRoute) to pick up custom claims.
    const token = await auth.currentUser.getIdToken(false);
    return token;
  } catch (error) {
    logger.error('secureApiClient', 'Failed to get access token', error?.message);
    throw error;
  }
}

/**
 * Make a secure API request with automatic token attachment
 *
 * Usage:
 *   const response = await secureApiCall('/api/admin/firestore/manualAlerts', {
 *     method: 'POST',
 *     body: { level: 'WARNING', message: '...' }
 *   });
 */
export async function secureApiCall(endpoint, options = {}) {
  try {
    // ✅ Step 1: Get access token (JWT)
    const token = await getAccessToken();

    // ✅ Step 2: Prepare headers with token (NOT password)
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`, // ← Token sent, not password
      ...options.headers,
    };

    // ✅ Step 3: Make request
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    // ✅ Step 4: Handle response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('secureApiClient', `Request failed to ${endpoint}`, error?.message);
    throw error;
  }
}

/**
 * Log a successful authentication event via secure API call
 *
 * ✅ SECURITY: Only logs LOGIN_SUCCESS events
 * - Prevents attackers from seeing what emails were tried
 * - Prevents attackers from seeing failed login patterns
 * - Only calls server AFTER token is obtained
 *
 * This logs with the token, not the password
 */
export async function secureLogAuthEvent(eventType, email, status) {
  try {
    // ✅ Only log successful logins
    if (eventType !== 'LOGIN_SUCCESS') {
      logger.warn('secureApiClient', `Skipping event log: ${eventType} (only LOGIN_SUCCESS is logged)`);
      return;
    }

    await secureApiCall('/api/auth/login-event', {
      method: 'POST',
      body: {
        eventType,
        email,
        status,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    logger.warn('secureApiClient', 'Failed to log auth event', error?.message);
    // Don't throw - logging shouldn't break the app
  }
}

/**
 * Create a secure admin operation via the server
 */
export async function secureAdminCreate(collectionId, data) {
  return secureApiCall(`/api/admin/firestore/${collectionId}`, {
    method: 'POST',
    body: { data },
  });
}

/**
 * Update via secure API
 */
export async function secureAdminUpdate(collectionId, docId, data) {
  return secureApiCall(`/api/admin/firestore/${collectionId}/${docId}`, {
    method: 'PATCH',
    body: { data },
  });
}

/**
 * Delete via secure API
 */
export async function secureAdminDelete(collectionId, docId) {
  return secureApiCall(`/api/admin/firestore/${collectionId}/${docId}`, {
    method: 'DELETE',
  });
}

/**
 * Delete all documents via secure API
 */
export async function secureAdminDeleteAll(collectionId) {
  return secureApiCall(`/api/admin/firestore/${collectionId}`, {
    method: 'DELETE',
  });
}

/**
 * Subscribe to SMS via secure API
 */
export async function secureSmsSubscribe({ phone, name, role }) {
  return secureApiCall('/api/admin/sms-subscribe', {
    method: 'POST',
    body: { phone, name, role },
  });
}

export async function secureListAdminUsers() {
  return secureApiCall('/api/admin/users', { method: 'GET' });
}

export async function secureCreateAdminUser({ email, password, displayName }) {
  return secureApiCall('/api/admin/users', {
    method: 'POST',
    body: { email, password, displayName },
  });
}

export async function secureDeleteAdminUser(uid) {
  return secureApiCall(`/api/admin/users/${uid}`, { method: 'DELETE' });
}

export async function secureGrantAdminClaim(uid) {
  return secureApiCall(`/api/admin/users/${uid}/grant-admin`, { method: 'POST' });
}

const secureApiClient = {
  getAccessToken,
  secureApiCall,
  secureLogAuthEvent,
  secureAdminCreate,
  secureAdminUpdate,
  secureAdminDelete,
  secureAdminDeleteAll,
  secureSmsSubscribe,
  secureListAdminUsers,
  secureCreateAdminUser,
  secureDeleteAdminUser,
};

export default secureApiClient;
